import { Component, OnInit, AfterViewInit, ViewChild, inject, ChangeDetectorRef, ElementRef, NgZone, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { embedDashboard, type EmbeddedDashboard } from '@superset-ui/embedded-sdk';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { AdminSidebarComponent } from '../sidebar/admin-sidebar.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { AssetRequestComponent } from '../../shared/components/asset-request/asset-request.component';
import { CreatedPoliciesComponent } from '../../org-admin/access-requests/created-policies/created-policies.component';
import { AclApdApiService } from '@angular-workspace/services';
import { KeycloakService } from 'keycloak-angular';
import { SupersetService } from '../../services/superset.service';
import { environment } from '../../../environments/environment';
import { WorkspaceContextService } from '../../services/workspace-context.service';

@Component({
  selector: 'app-admin-access-requests',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    AdminSidebarComponent,
    PageHeaderComponent,
    AssetRequestComponent,
    CreatedPoliciesComponent,
  ],
  templateUrl: './access-requests.component.html',
  styleUrl: './access-requests.component.scss',
})
export class AdminAccessRequestsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(AssetRequestComponent) assetRequestComponent!: AssetRequestComponent;
  @ViewChild('analyticsMount')
  set analyticsMountRef(ref: ElementRef<HTMLDivElement> | undefined) {
    this._analyticsMount = ref;
    if (ref && this.showAnalytics) {
      void this.embedAnalytics();
    }
  }
  private _analyticsMount?: ElementRef<HTMLDivElement>;

  private aclApdApiService = inject(AclApdApiService);
  private cdr = inject(ChangeDetectorRef);
  private keycloak = inject(KeycloakService);
  private supersetService = inject(SupersetService);
  private ngZone = inject(NgZone);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  protected readonly workspace = inject(WorkspaceContextService);

  activeTab: 'assetRequest' | 'createdPolicies' = 'assetRequest';
  private embeddedDashboard: EmbeddedDashboard | null = null;
  private embedToken = 0;
  readonly aclDashboardId = environment.datasetAccessRequestsSupersetDashboardId;
  showAnalytics = false;
  analyticsLoading = false;
  analyticsError: string | null = null;

  async ngOnInit(): Promise<void> {
    const tabFromUrl = this.route.snapshot.queryParamMap.get('tab');
    if (tabFromUrl === 'assetRequest' || tabFromUrl === 'createdPolicies') {
      this.activeTab = tabFromUrl;
    }
  }

  setActiveTab(tab: 'assetRequest' | 'createdPolicies'): void {
    if (this.activeTab === tab) {
      return;
    }
    this.activeTab = tab;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });

    if (tab === 'assetRequest') {
      void this.loadAssetRequestsWhenReady();
    }
  }

  async ngAfterViewInit(): Promise<void> {
    // Wait for next tick to ensure child component is fully initialized
    await Promise.resolve();

    // Wait for Keycloak to be ready before making API calls
    await this.waitForKeycloakReady();

    if (this.activeTab === 'assetRequest') {
      await this.loadAssetRequestsWhenReady();
    }
  }

  private async loadAssetRequestsWhenReady(): Promise<void> {
    for (let attempt = 0; attempt < 25; attempt++) {
      this.cdr.detectChanges();
      if (this.assetRequestComponent) {
        await this.loadAssetRequests();
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 16));
    }
  }

  private async waitForKeycloakReady(): Promise<void> {
    try {
      const keycloakInstance = this.keycloak.getKeycloakInstance();
      if (keycloakInstance?.authenticated && keycloakInstance?.token) {
        return;
      }

      const maxWaitTime = 5000;
      const checkInterval = 100;
      const startTime = Date.now();

      while (Date.now() - startTime < maxWaitTime) {
        const instance = this.keycloak.getKeycloakInstance();
        if (instance?.authenticated && instance?.token) {
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, checkInterval));
      }
    } catch (error) {
      console.error('Error waiting for Keycloak:', error);
    }
  }

  async loadAssetRequests(): Promise<void> {
    try {
      if (!this.assetRequestComponent) {
        return;
      }

      const page = this.assetRequestComponent.pagination?.currentPage || 1;
      const size = this.assetRequestComponent.pagination?.pageSize || 10;
      const dateRange = this.assetRequestComponent.getDateRangeForApi();
      const organizationId = this.assetRequestComponent.getOrganisationIdForApi();
      const requestStatus = this.assetRequestComponent.getRequestStatusForApi();
      const isExpired = this.assetRequestComponent.selectedRequestStatus === 'EXPIRED';
      const explicitSort = this.assetRequestComponent.getSortQueryString()?.trim();
      const sortParam = explicitSort || undefined;

      const response = await this.aclApdApiService.platformAssetRequests(
        page,
        size,
        isExpired ? undefined : requestStatus,
        dateRange?.startDate,
        dateRange?.endDate,
        organizationId,
        sortParam,
        isExpired,
      );

      this.assetRequestComponent.mapProviderAssetRequestsResponse(response);
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error loading admin asset requests:', error);
      if (this.assetRequestComponent) {
        this.assetRequestComponent.loading = false;
      }
    }
  }

  toggleAnalyticsView(showAnalytics: boolean): void {
    if (this.showAnalytics === showAnalytics) return;
    this.showAnalytics = showAnalytics;
    if (!showAnalytics) {
      this.teardownEmbed();
      return;
    }
    setTimeout(() => void this.embedAnalytics(), 0);
  }

  private async embedAnalytics(): Promise<void> {
    if (!this.showAnalytics) return;
    const supersetDomain = environment.supersetDashboardUrl?.trim();
    const guestTokenEndpoint = environment.supersetGuestTokenEndpoint?.trim();
    const mountEl = this._analyticsMount?.nativeElement;

    if (!mountEl || !supersetDomain || !guestTokenEndpoint) {
      this.analyticsError = supersetDomain
        ? null
        : 'Superset is not configured (supersetDashboardUrl missing).';
      return;
    }

    const token = ++this.embedToken;
    this.analyticsLoading = true;
    this.analyticsError = null;
    this.teardownEmbed(false, false);

    try {
      let guestTokenPromise: Promise<string> | null = this.supersetService.getGuestToken();
      const embedUuid = await this.supersetService.resolveEmbedUuid(this.aclDashboardId);

      if (token !== this.embedToken) {
        return;
      }

      const embedded = await embedDashboard({
        id: embedUuid,
        supersetDomain: supersetDomain.replace(/\/+$/, ''),
        mountPoint: mountEl,
        fetchGuestToken: () => {
          const pending = guestTokenPromise ?? this.supersetService.getGuestToken();
          guestTokenPromise = null;
          return pending;
        },
        dashboardUiConfig: {
          hideTitle: true,
          urlParams: { standalone: '1', native_filters_key: '' },
        },
      });

      if (token !== this.embedToken) {
        embedded.unmount();
        return;
      }
      this.embeddedDashboard = embedded;
    } catch {
      if (token === this.embedToken) {
        this.analyticsError = 'Unable to load analytics. Please check guest token middleware.';
      }
    } finally {
      if (token === this.embedToken) {
        // embedDashboard() resolves off an iframe 'load'/postMessage handshake, which can
        // settle outside Angular's zone and leave the UI showing a stale spinner until the
        // next unrelated event. Re-enter the zone explicitly so the view updates immediately.
        this.ngZone.run(() => {
          this.analyticsLoading = false;
        });
      }
    }
  }

  private teardownEmbed(clearMount = true, incrementToken = true): void {
    if (incrementToken) {
      this.embedToken++;
    }
    this.embeddedDashboard?.unmount();
    this.embeddedDashboard = null;
    if (clearMount && this._analyticsMount?.nativeElement) {
      this._analyticsMount.nativeElement.innerHTML = '';
    }
  }

  ngOnDestroy(): void {
    this.teardownEmbed();
  }
}
